import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * Cari modülünün panel tarafındaki SÖZLEŞMESİ: sunucu tipleri, seçenek
 * sözlükleri ve veri kancaları.
 *
 * Neden tek dosya: cari kaydındaki dört alan (`customerType`, `countryCode`,
 * `vatNumber`, `filesPeriodicReturns`) KDV çözümleyicisinin GİRDİSİDİR ve
 * yanlış değer üç ay sonra, Intervat dosyası reddedildiğinde (ya da hiç
 * reddedilmeyip EKSİK BEYAN olarak kalarak) görünür. Bu yüzden her seçeneğin
 * HUKUKİ SONUCU burada, seçeneğin yanında yazılıdır ve iki form (yeni cari
 * kutusu + detay formu) aynı metni okur. İki kopya kalsaydı biri düzeltilip
 * öteki unutulurdu.
 *
 * ⚠ Buradaki hiçbir liste bir KAPI değildir. Sunucu `contacts.dto.ts` +
 * `contacts.service.ts` + DB kısıtlarıyla kendi kapısını kurar; buradakiler
 * operatöre neyi seçtiğini ANLATMAK içindir.
 */

// ───────────────────────────────────────────────────────────── birlikler
//
// ⚠ Bu diziler `apps/api/src/modules/vat/vat-types.ts` ve
// `entities/contact.entity.ts` ile BİREBİR aynı olmak zorunda. Paylaşılan bir
// pakete taşımak M2'nin işi; bugün panel API'nin `dist`ini import edemiyor.

export const CUSTOMER_TYPES = ['B2B_TAXABLE', 'B2C', 'NON_TAXABLE_LEGAL', 'PUBLIC_BODY'] as const;
export type CustomerType = (typeof CUSTOMER_TYPES)[number];

export const VAT_TREATMENTS = [
  'DOMESTIC',
  'DOMESTIC_RC_IMMOVABLE',
  'EU_B2B_RC',
  'INTRA_EU_SUPPLY_GOODS',
  'EU_B2C_OSS',
  'OUTSIDE_EU',
  'EXPORT',
  'EXEMPT_SMALL_BUSINESS',
  'EXEMPT_ART44',
] as const;
export type VatTreatment = (typeof VAT_TREATMENTS)[number];

/** ⚠ İLETİŞİM dili. Fatura dili DEĞİL — onu işletme merkezinin bölgesi belirler. */
export const CONTACT_LANGUAGES = ['nl', 'fr', 'en', 'de'] as const;
export type ContactLanguage = (typeof CONTACT_LANGUAGES)[number];

/** Faturaya DONACAK durum. `UNAVAILABLE` ile `CHECKED_INVALID` ayrı kalır. */
export type ViesStatus = 'CHECKED_VALID' | 'CHECKED_INVALID' | 'UNAVAILABLE' | 'NOT_APPLICABLE';

/** Servisin ince teşhisi — `status`tan ayrıntılı, operatör mesajı buna dallanır. */
export type ViesOutcome = 'valid' | 'not_found' | 'invalid_format' | 'unavailable' | 'no_number';

// ─────────────────────────────────────────────────────────── sunucu tipleri

/**
 * `contacts.viesCheck` jsonb kaydı — SON sorgunun sonucu.
 *
 * ⚠ `valid: null` = SORULAMADI, `valid: false` = SORULDU VE GEÇERSİZ.
 * Bu ikisi ASLA aynı kovaya konmaz: üye devlet sunucuları düzenli olarak
 * kapanır ve `false` göstermek meşru bir kurumsal müşteriyi "sahte numaralı"
 * gibi göstermek olur.
 */
export interface ViesCheckRecord {
  checkedAt: string;
  countryCode: string;
  vatNumber: string;
  valid: boolean | null;
  /** VIES'in verdiği TEK yasal kanıt (qualified confirmation). */
  requestIdentifier: string | null;
  name: string | null;
  address: string | null;
  source: string;
  error: string | null;
}

export interface Contact {
  id: string;
  name: string;
  legalName: string | null;
  isCustomer: boolean;
  isSupplier: boolean;
  customerType: CustomerType | null;
  vatNumber: string | null;
  enterpriseNumber: string | null;
  countryCode: string;
  filesPeriodicReturns: boolean;
  language: ContactLanguage;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  countrySubdivision: string | null;
  peppolParticipantId: string | null;
  /** ⚠ `null` = HİÇ BAKILMADI; `false` = SMP'de bulunamadı. Aynı şey değil. */
  peppolCapable: boolean | null;
  peppolCheckedAt: string | null;
  peppolIncapableReason: string | null;
  viesCheck: ViesCheckRecord | null;
  paymentTermDays: number;
  iban: string | null;
  bic: string | null;
  email: string | null;
  phone: string | null;
  /**
   * ⚠ PEPPOL DIŞI KANALIN DAYANAĞI: müşteri faturayı e-postayla almayı kabul
   * ettiyse üçü de doludur. Yarım kayıt sunucuda reddedilir.
   */
  einvoiceEmailAddress: string | null;
  einvoiceEmailConsentAt: string | null;
  einvoiceEmailConsentNote: string | null;
  defaultVatTreatment: VatTreatment | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContactListResponse {
  items: Contact[];
  total: number;
  limit: number;
  offset: number;
}

/** `POST /contacts/:id/vies-check` yanıtı. ⚠ Ulaşılamama da 200 döner. */
export interface ViesCheckResponse {
  contactId: string;
  vatNumber: string | null;
  status: ViesStatus;
  outcome: ViesOutcome;
  checkedAt: string;
  countryCode: string | null;
  number: string | null;
  /** Sorgu KANIT üretti mi — kendi KDV numaramız gönderilmediyse `false`. */
  qualified: boolean;
  requestIdentifier: string | null;
  name: string | null;
  address: string | null;
  error: string | null;
  fromCache: boolean;
  /** Sonuç cariye yazıldı mı (numara ayrıştırılamadıysa yazılmaz). */
  stored: boolean;
}

/**
 * Yazma gövdesi.
 *
 * ⚠ Sunucuda `whitelist: true` + `forbidNonWhitelisted: true` — burada
 * olmayan bir anahtar göndermek isteğin TAMAMINI 400 ile düşürür. Yani bu
 * arayüz "gönderilebilecek alanların tamamı"dır; `peppolCapable`,
 * `viesCheck`, `createdAt` gibi alanlar BİLEREK yoktur.
 *
 * ⚠ `null` = ALANI TEMİZLE, `undefined` = DOKUNMA. Ayrım gerçektir: boş dize
 * yazmak "e-postası yok" ile "e-postası boş metin" ayrımını yok ederdi.
 */
export interface ContactWrite {
  name?: string;
  countryCode?: string;
  legalName?: string | null;
  isCustomer?: boolean;
  isSupplier?: boolean;
  customerType?: CustomerType | null;
  vatNumber?: string | null;
  enterpriseNumber?: string | null;
  filesPeriodicReturns?: boolean;
  language?: ContactLanguage;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  countrySubdivision?: string | null;
  peppolParticipantId?: string | null;
  paymentTermDays?: number;
  iban?: string | null;
  bic?: string | null;
  email?: string | null;
  phone?: string | null;
  einvoiceEmailAddress?: string | null;
  einvoiceEmailConsentAt?: string | null;
  einvoiceEmailConsentNote?: string | null;
  defaultVatTreatment?: VatTreatment | null;
  notes?: string | null;
  active?: boolean;
}

/** Oluştururken ad ve ülke ZORUNLU; güncellerken değil. */
export type CreateContactBody = ContactWrite & { name: string; countryCode: string };

// ─────────────────────────────────────────── seçenek sözlükleri (hukuki not)

export interface ChoiceInfo<T extends string> {
  value: T;
  label: string;
  /** Seçimin SONUCU — tanımı değil. Formda seçeneğin altında görünür. */
  note: string;
}

/**
 * Müşteri tipi — KDV'nin NE ZAMAN muaccel olduğunu ve hangi dalın
 * seçileceğini belirleyen alan.
 *
 * ⚠ `PUBLIC_BODY` ayrı bir seçenektir ve `NON_TAXABLE_LEGAL` ile
 * BİRLEŞTİRİLEMEZ: md. 22bis §4 kamu kurumuna KASA ESASI dayatır. Fatura
 * tarihi etkisizdir; belge, tahsilat gerçekleşene kadar hiçbir KDV dönemine
 * düşmez.
 */
export const CUSTOMER_TYPE_CHOICES: ReadonlyArray<ChoiceInfo<CustomerType>> = [
  {
    value: 'B2B_TAXABLE',
    label: 'KDV mükellefi işletme (B2B)',
    note:
      'KDV fatura tarihinde muaccel olur. AB ülkesindeyse ve KDV numarası VIES ile ' +
      'doğrulanmışsa ters yükümlülük dalına girer (kutu 44 hizmet, kutu 46 mal).',
  },
  {
    value: 'B2C',
    label: 'Nihai tüketici (B2C)',
    note:
      'md. 22bis §3 KASA ESASI: KDV tahsilatta muaccel olur. AB tüketicisine ' +
      'elektronik hizmette OSS dalı (kutu 47) işler.',
  },
  {
    value: 'NON_TAXABLE_LEGAL',
    label: 'Mükellef olmayan tüzel kişi',
    note:
      'KDV numarası yoktur ya da ters yükümlülüğe uygun değildir; AB satışı B2B ' +
      'gibi işlem GÖRMEZ. Kamu kurumu ise bu seçenek değil, aşağıdaki seçilir.',
  },
  {
    value: 'PUBLIC_BODY',
    label: 'Kamu kurumu',
    note:
      '⚠ md. 22bis §4 KASA ESASI dayatır: fatura tarihi ETKİSİZDİR, KDV ancak ' +
      'TAHSİLATTA muaccel olur. Belge tahsil edilene kadar hiçbir KDV dönemine ' +
      'düşmez — bu doğru davranıştır, eksik beyan değildir.',
  },
];

export const CUSTOMER_TYPE_LABELS: Record<CustomerType, string> = {
  B2B_TAXABLE: 'B2B mükellef',
  B2C: 'B2C tüketici',
  NON_TAXABLE_LEGAL: 'Mükellef olmayan tüzel kişi',
  PUBLIC_BODY: 'Kamu kurumu',
};

/**
 * KDV muamelesi override'ı — OTOMATİK ÇÖZÜMÜ EZER.
 *
 * Her satırın notu o muamelenin HANGİ KUTUYU ürettiğini söyler; yanlış seçim
 * yanlış Intervat kutusu demektir.
 */
export const VAT_TREATMENT_CHOICES: ReadonlyArray<ChoiceInfo<VatTreatment>> = [
  {
    value: 'DOMESTIC',
    label: 'Yurtiçi satış',
    note: 'Kutu 3/2/1 (matrah) → 54 (KDV). Normal %21 / %12 / %6.',
  },
  {
    value: 'DOMESTIC_RC_IMMOVABLE',
    label: 'Yurtiçi ters yükümlülük — taşınmaz işi',
    note:
      'Kutu 45. ŞARTI: alıcı Belçikalı mükellef VE periyodik KDV beyanı veriyor ' +
      '(md. 20 KB nr. 1). Şart yoksa normal %21 uygulanır.',
  },
  {
    value: 'EU_B2B_RC',
    label: 'AB B2B hizmet — ters yükümlülük',
    note:
      'Kutu 44, IC listing kodu S. MADDİ ŞART: alıcı başka bir AB ülkesinde ve ' +
      'KDV numarası VIES ile doğrulanmış olmalı.',
  },
  {
    value: 'INTRA_EU_SUPPLY_GOODS',
    label: "AB'ye mal teslimi (md. 39bis)",
    note:
      'Kutu 46, IC listing kodu L. MADDİ ŞART: doğrulanmış AB numarası + nakliye ' +
      'kanıtı. Kanıt yoksa idare KDV’yi satıcıdan ister.',
  },
  {
    value: 'EU_B2C_OSS',
    label: 'AB tüketicisine elektronik hizmet — OSS',
    note: 'Kutu 47. Varış ülkesinin oranı uygulanır ve ciro OSS beyanına yazılır.',
  },
  {
    value: 'OUTSIDE_EU',
    label: 'AB dışı hizmet — Belçika KDV kapsamı dışında',
    note: 'Kutu 47.',
  },
  {
    value: 'EXPORT',
    label: 'AB dışına mal ihracı (md. 39 §1)',
    note: 'Kutu 47. İhracat kanıtı olmadan muafiyet düşer.',
  },
  {
    value: 'EXEMPT_SMALL_BUSINESS',
    label: 'Küçük işletme muafiyeti (md. 56bis)',
    note: 'KDV hesaplanmaz; eşik aşılırsa muafiyet sona erer.',
  },
  {
    value: 'EXEMPT_ART44',
    label: 'md. 44 muafiyeti',
    note:
      'Otomatik TÜRETİLMEZ, yalnız elle seçilir. Muafiyet maddesinin metnini ' +
      'faturaya siz yazarsınız — çözümleyici üretmez.',
  },
];

export const VAT_TREATMENT_LABELS: Record<VatTreatment, string> = {
  DOMESTIC: 'Yurtiçi satış',
  DOMESTIC_RC_IMMOVABLE: 'Yurtiçi ters yükümlülük (taşınmaz)',
  EU_B2B_RC: 'AB B2B ters yükümlülük',
  INTRA_EU_SUPPLY_GOODS: "AB'ye mal teslimi",
  EU_B2C_OSS: 'AB B2C OSS',
  OUTSIDE_EU: 'AB dışı hizmet',
  EXPORT: 'İhracat',
  EXEMPT_SMALL_BUSINESS: 'Küçük işletme muafiyeti',
  EXEMPT_ART44: 'md. 44 muafiyeti',
};

export const CONTACT_LANGUAGE_LABELS: Record<ContactLanguage, string> = {
  nl: 'Felemenkçe',
  fr: 'Fransızca',
  en: 'İngilizce',
  de: 'Almanca',
};

// ─────────────────────────────────────────────────────────────── ülkeler

/**
 * Ülke listesi — YARDIMCI, kapı DEĞİL.
 *
 * ⚠ `eu` bayrağı yalnızca formda bilgi göstermek içindir. AB üyeliği
 * TARİH ETKİLİDİR (Brexit'te sabit bir bayrağın tüm satırları sessizce yanlış
 * kalırdı) ve kararı sunucu `common/vat/eu-vat.ts` ile verir, faturaya
 * `billingIsEu` olarak DONDURUR. Buradaki bayrağa hiçbir hesap bağlanmaz.
 *
 * ⚠ ISO kodu ile KDV ÖNEKİ farklı olabilir: Yunanistan'ın ülke kodu `GR`,
 * KDV numarası öneki `EL`'dir. `countryCode` alanına `GR`, `vatNumber`
 * alanına `EL…` yazılır.
 *
 * ⚠ `XI` (Kuzey İrlanda) geçerli bir değerdir ve YARIM AB'dir: MAL ticaretinde
 * AB kuralları (kutu 46), HİZMETTE AB dışı (kutu 47). Ayrımı sunucu yapar.
 */
export interface CountryChoice {
  code: string;
  label: string;
  eu: boolean;
}

export const COUNTRY_CHOICES: ReadonlyArray<CountryChoice> = [
  { code: 'BE', label: 'Belçika', eu: true },
  { code: 'AT', label: 'Avusturya', eu: true },
  { code: 'BG', label: 'Bulgaristan', eu: true },
  { code: 'CY', label: 'Kıbrıs', eu: true },
  { code: 'CZ', label: 'Çekya', eu: true },
  { code: 'DE', label: 'Almanya', eu: true },
  { code: 'DK', label: 'Danimarka', eu: true },
  { code: 'EE', label: 'Estonya', eu: true },
  { code: 'ES', label: 'İspanya', eu: true },
  { code: 'FI', label: 'Finlandiya', eu: true },
  { code: 'FR', label: 'Fransa', eu: true },
  { code: 'GR', label: 'Yunanistan (KDV öneki EL)', eu: true },
  { code: 'HR', label: 'Hırvatistan', eu: true },
  { code: 'HU', label: 'Macaristan', eu: true },
  { code: 'IE', label: 'İrlanda', eu: true },
  { code: 'IT', label: 'İtalya', eu: true },
  { code: 'LT', label: 'Litvanya', eu: true },
  { code: 'LU', label: 'Lüksemburg', eu: true },
  { code: 'LV', label: 'Letonya', eu: true },
  { code: 'MT', label: 'Malta', eu: true },
  { code: 'NL', label: 'Hollanda', eu: true },
  { code: 'PL', label: 'Polonya', eu: true },
  { code: 'PT', label: 'Portekiz', eu: true },
  { code: 'RO', label: 'Romanya', eu: true },
  { code: 'SE', label: 'İsveç', eu: true },
  { code: 'SI', label: 'Slovenya', eu: true },
  { code: 'SK', label: 'Slovakya', eu: true },
  { code: 'XI', label: 'Kuzey İrlanda (yalnız malda AB)', eu: true },
  { code: 'GB', label: 'Birleşik Krallık', eu: false },
  { code: 'CH', label: 'İsviçre', eu: false },
  { code: 'NO', label: 'Norveç', eu: false },
  { code: 'IS', label: 'İzlanda', eu: false },
  { code: 'LI', label: 'Lihtenştayn', eu: false },
  { code: 'TR', label: 'Türkiye', eu: false },
  { code: 'US', label: 'Amerika Birleşik Devletleri', eu: false },
  { code: 'CA', label: 'Kanada', eu: false },
  { code: 'BR', label: 'Brezilya', eu: false },
  { code: 'MX', label: 'Meksika', eu: false },
  { code: 'AR', label: 'Arjantin', eu: false },
  { code: 'MA', label: 'Fas', eu: false },
  { code: 'DZ', label: 'Cezayir', eu: false },
  { code: 'TN', label: 'Tunus', eu: false },
  { code: 'EG', label: 'Mısır', eu: false },
  { code: 'ZA', label: 'Güney Afrika', eu: false },
  { code: 'NG', label: 'Nijerya', eu: false },
  { code: 'SN', label: 'Senegal', eu: false },
  { code: 'AE', label: 'Birleşik Arap Emirlikleri', eu: false },
  { code: 'SA', label: 'Suudi Arabistan', eu: false },
  { code: 'QA', label: 'Katar', eu: false },
  { code: 'KW', label: 'Kuveyt', eu: false },
  { code: 'IL', label: 'İsrail', eu: false },
  { code: 'LB', label: 'Lübnan', eu: false },
  { code: 'JO', label: 'Ürdün', eu: false },
  { code: 'RU', label: 'Rusya', eu: false },
  { code: 'UA', label: 'Ukrayna', eu: false },
  { code: 'RS', label: 'Sırbistan', eu: false },
  { code: 'BA', label: 'Bosna-Hersek', eu: false },
  { code: 'ME', label: 'Karadağ', eu: false },
  { code: 'MK', label: 'Kuzey Makedonya', eu: false },
  { code: 'AL', label: 'Arnavutluk', eu: false },
  { code: 'MD', label: 'Moldova', eu: false },
  { code: 'GE', label: 'Gürcistan', eu: false },
  { code: 'AZ', label: 'Azerbaycan', eu: false },
  { code: 'KZ', label: 'Kazakistan', eu: false },
  { code: 'IN', label: 'Hindistan', eu: false },
  { code: 'CN', label: 'Çin', eu: false },
  { code: 'HK', label: 'Hong Kong', eu: false },
  { code: 'TW', label: 'Tayvan', eu: false },
  { code: 'JP', label: 'Japonya', eu: false },
  { code: 'KR', label: 'Güney Kore', eu: false },
  { code: 'SG', label: 'Singapur', eu: false },
  { code: 'MY', label: 'Malezya', eu: false },
  { code: 'ID', label: 'Endonezya', eu: false },
  { code: 'TH', label: 'Tayland', eu: false },
  { code: 'VN', label: 'Vietnam', eu: false },
  { code: 'PH', label: 'Filipinler', eu: false },
  { code: 'PK', label: 'Pakistan', eu: false },
  { code: 'AU', label: 'Avustralya', eu: false },
  { code: 'NZ', label: 'Yeni Zelanda', eu: false },
];

const COUNTRY_BY_CODE = new Map(COUNTRY_CHOICES.map((c) => [c.code, c]));

/** Kod tanınmıyorsa `null` — liste kısadır, sunucu her iki harfli kodu kabul eder. */
export function countryChoice(code: string | null | undefined): CountryChoice | null {
  if (!code) return null;
  return COUNTRY_BY_CODE.get(code.trim().toUpperCase()) ?? null;
}

/** Tablo ve özet satırları için: tanınan kodda "Belçika (BE)", tanınmayanda ham kod. */
export function countryLabel(code: string | null | undefined): string {
  if (!code) return '—';
  const upper = code.trim().toUpperCase();
  const found = COUNTRY_BY_CODE.get(upper);
  return found ? `${found.label} (${upper})` : upper;
}

// ────────────────────────────────────── override × ülke uyuşmazlığı

/**
 * ⚠ AB MUAMELESİ YURTİÇİ CARİYE YAZILAMAZ.
 *
 * Kayıt bu değerle KAYDEDİLİR ama fatura DÜZENLENEMEZ: `resolveVatTreatment`
 * override'ı denetler ve bloke edici döner (`EU_B2B_VIES_UNVERIFIED`,
 * `EU_B2C_OSS_NOT_APPLICABLE`). Panelin bunu kaydetme anında söylemesi,
 * operatörün hatayı üç ay sonra fatura kesemediğinde keşfetmesine yeğdir.
 *
 * Sebep hukuki: Belçikalı bir cariye `EU_B2B_RC` yazmak yurtiçi bir satışı AB
 * ters yükümlülüğü gibi beyan eder, IC listing'de OLMAYAN bir AB işlemi
 * bildirir ve %21 KDV hiç tahsil edilmez. `EU_B2C_OSS` bunun en tehlikelisidir
 * çünkü zorunlu ibaresi yoktur — belge gerçekten düzenlenip DONABİLİR ve
 * düzeltmenin tek yolu kredi notudur.
 */
export function vatTreatmentCountryConflict(
  countryCode: string,
  treatment: VatTreatment | null,
): string | null {
  const country = countryCode.trim().toUpperCase();
  if (country !== 'BE' || treatment === null) return null;

  if (treatment === 'EU_B2B_RC' || treatment === 'INTRA_EU_SUPPLY_GOODS') {
    return (
      'Yurtiçi (BE) cariye AB ters yükümlülüğü yazılamaz. Kayıt edilir ama bu cariye ' +
      'fatura DÜZENLENEMEZ: çözümleyici EU_B2B_VIES_UNVERIFIED bloke edicisini üretir.'
    );
  }
  if (treatment === 'EU_B2C_OSS') {
    return (
      'Yurtiçi (BE) cariye OSS yazılamaz — yurtiçi satış kutu 47’ye (OSS cirosu) düşer ve ' +
      '%21 KDV hiç tahsil edilmez. Çözümleyici EU_B2C_OSS_NOT_APPLICABLE ile durdurur.'
    );
  }
  return null;
}

// ─────────────────────────────────────────────────────────── sorgu anahtarı

export interface ListContactsParams {
  limit?: number;
  offset?: number;
  q?: string;
  isCustomer?: boolean;
  isSupplier?: boolean;
  active?: boolean;
}

export const contactKeys = {
  /** Kök: bir mutasyondan sonra liste + detay birlikte tazelensin. */
  all: ['contacts'] as const,
  list: (params: ListContactsParams) => ['contacts', 'list', params] as const,
  detail: (id: string) => ['contacts', 'detail', id] as const,
};

/**
 * Sorgu dizesi. ⚠ Boolean'lar `'true'`/`'false'` METNİ olarak gider: sunucu
 * `queryBoolean` ile ELLE çevirir, çünkü `Boolean('false') === true` tuzağı
 * "pasifleri göster" süzgecini sessizce tersine çevirirdi.
 */
function buildQuery(params: ListContactsParams): string {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  if (params.q) search.set('q', params.q);
  if (params.isCustomer !== undefined) search.set('isCustomer', String(params.isCustomer));
  if (params.isSupplier !== undefined) search.set('isSupplier', String(params.isSupplier));
  if (params.active !== undefined) search.set('active', String(params.active));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

// ───────────────────────────────────────────────────────────────── kancalar

export function useContacts(params: ListContactsParams) {
  return useQuery({
    queryKey: contactKeys.list(params),
    queryFn: () => api.get<ContactListResponse>(`/contacts${buildQuery(params)}`),
    /*
     * Sayfa/süzgeç değişince tablo BOŞALMAZ, eski sonuç soluk kalır. Boşaltmak
     * satır yüksekliğini zıplatır ve operatör tıklamak üzereyken hedefi kayar.
     */
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: contactKeys.detail(id ?? ''),
    queryFn: () => api.get<Contact>(`/contacts/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * ⚠ Mutasyonlar `contactKeys.all` kökünü geçersizler — liste VE detay birlikte.
 * Sunucu değerleri KANONİKLEŞTİRİR (KDV numarası önek + ayraçsız gövdeye,
 * IBAN büyük harfe, e-posta küçük harfe döner); yerel formu "başarılı" sayıp
 * tazelemezsek operatör gönderdiği ham metni görmeye devam eder ve kaydın
 * gerçekte ne olduğunu bilemez.
 */
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContactBody) => api.post<Contact>('/contacts', body),
    onSuccess: (saved) => {
      qc.setQueryData(contactKeys.detail(saved.id), saved);
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ContactWrite }) =>
      api.patch<Contact>(`/contacts/${id}`, patch),
    onSuccess: (saved) => {
      qc.setQueryData(contactKeys.detail(saved.id), saved);
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true; id: string }>(`/contacts/${id}`),
    onSuccess: (_res, id) => {
      qc.removeQueries({ queryKey: contactKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}

/**
 * VIES sorgusu.
 *
 * ⚠ POST'tur ve bu bilinçlidir: sonucu cariye YAZAR ve dış bir servise gider.
 * ⚠ Ulaşılamama HATA DEĞİLDİR — sunucu yine 200 döner ve `outcome:
 * 'unavailable'` taşır. Bu yüzden `onError` yalnız gerçek arızalar için
 * çalışır; üç durumu ayırmak ÇAĞIRAN EKRANIN işidir.
 */
export function useViesCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      api.post<ViesCheckResponse>(`/contacts/${id}/vies-check`, { force }),
    onSuccess: () => {
      /*
       * Sunucu `contacts.viesCheck` alanını GÜNCELLEDİ. Yalnız DETAY
       * geçersizlenirse liste bayat kalır ve oradaki VIES rozeti bir önceki
       * sonucu göstermeye devam eder — operatör az önce doğruladığı numarayı
       * listede hâlâ kırmızı görür. Dosyadaki öteki mutasyonlarla aynı kalıp:
       * kök geçersizlenir.
       */
      void qc.invalidateQueries({ queryKey: contactKeys.all });
    },
  });
}
